import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../axiosInstance";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import IconButton from "../../../components/IconButton/IconButton";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";
import { Country, State, City } from "country-state-city";
import "./Branch.scss";
import { toast } from "react-toastify";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import GeneralTable from "../../../components/Table/GeneralTable";

const Branch = ({ markPageAsCompleted }) => {
  const [branches, setBranches] = useState([]);
  const [editBranch, setEditBranch] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [showWarningPopup, setShowWarningPopup] = useState(false);

  const fetchBranches = useCallback(async () => {
    setError("");
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/branches`,
      );
      if (response.status === 200) {
        const branchesData = response.data?.data || [];
        if (branchesData.length > 0) {
          const updatedBranches = branchesData.map((branch) => ({
            ...branch,
            status:
              branch.status?.toLowerCase() === "enabled"
                ? "Enabled"
                : "Disabled",
          }));
          setBranches(updatedBranches);
          const enabledBranches = updatedBranches.filter(
            (branch) => branch.status === "Enabled",
          );
          if (enabledBranches.length >= 1) {
            markPageAsCompleted("Branch");
          }
        } else {
          console.error("No branches found or empty data:", response.data);
        }
      }
    } catch (error) {
      setError("Failed to fetch branches. Please try again later");
    }
  }, [markPageAsCompleted]); // Dependency for useCallback

  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/departments/all`,
      );
      if (response.status === 200) {
        setDepartments(response.data.data || []);
      } else {
        console.error("Error fetching departments:", response.message);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      setError("Failed to fetch departments. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []); // This function has no dependencies

  // 3. Add the stable functions to the useEffect dependency array
  useEffect(() => {
    fetchBranches();
    fetchDepartments();
  }, [fetchBranches, fetchDepartments]);

  const handleCheckboxChange = (branchId, event) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setSelectedBranches([...selectedBranches, branchId]);
    } else {
      setSelectedBranches(selectedBranches.filter((id) => id !== branchId));
    }
  };

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      const allIdsOnPage = displayedBranches.map((branch) => branch.branchId);
      setSelectedBranches((prevSelected) => {
        const uniqueIds = Array.from(
          new Set([...prevSelected, ...allIdsOnPage]),
        );
        return uniqueIds;
      });
    } else {
      const idsOnPage = displayedBranches.map((branch) => branch.branchId);
      setSelectedBranches((prevSelected) =>
        prevSelected.filter((id) => !idsOnPage.includes(id)),
      );
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedBranches.length === 0) {
      toast.error("Please select branches to delete.");
      return;
    }
    setShowWarningPopup(true);
  };

  const handleBulkDeleteSubmit = async () => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/branches`,
        { data: selectedBranches },
      );
      await fetchBranches();
      setSelectedBranches([]);
      toast.success("Selected branches deleted successfully.");
    } catch (error) {
      console.error("Error deleting branches:", error);
    } finally {
      setShowWarningPopup(false);
    }
  };

  const normalizeCityName = (cityName) => {
    if (cityName === "Bangalore") {
      return "Bengaluru";
    }
    return cityName;
  };

  const getInitialValues = () => {
    if (editBranch) {
      return {
        branchId: editBranch.branchId,
        branchName: editBranch.branchName,
        branchState: editBranch.branchState,
        companyId: process.env.REACT_APP_COMPANY_ID,
        branchAddress: {
          addressId: editBranch.branchAddress?.addressId || 0,
          addressLine1: editBranch.branchAddress?.addressLine1 || "",
          addressLine2: editBranch.branchAddress?.addressLine2 || "",
          country: editBranch.branchAddress?.country || "IN",
          state: editBranch.branchAddress?.state || "KA",
          city:
            normalizeCityName(editBranch.branchAddress?.city) || "Bengaluru",
          pincode: editBranch.branchAddress?.pincode || "",
        },
        departmentIds: editBranch.departmentIds || [],
      };
    }
    return {
      branchId: 0,
      branchName: "",
      branchState: "",
      companyId: process.env.REACT_APP_COMPANY_ID,
      branchAddress: {
        addressId: 0,
        addressLine1: "",
        addressLine2: "",
        country: "IN",
        state: "KA",
        city: "Bengaluru",
        pincode: "",
      },
      departmentIds: [],
    };
  };

  const validationSchema = Yup.object().shape({
    branchName: Yup.string().required("Branch name is required"),
    branchAddress: Yup.object().shape({
      addressLine1: Yup.string().required("Address Line 1 is required"),
      addressLine2: Yup.string(),
      country: Yup.string().required("Country is required"),
      state: Yup.string().required("State is required"),
      city: Yup.string().required("City is required"),
      pincode: Yup.string()
        .matches(/^\d+$/, "Pincode must be a number")
        .required("Pincode is required"),
    }),
    departmentIds: Yup.array().min(
      1,
      "At least one department must be selected",
    ),
  });

  const handleToggle = async (branchId, currentStatus) => {
    const newStatus = currentStatus === "Enabled" ? "DISABLED" : "ENABLED";
    try {
      await handleStatusChange(branchId, newStatus);
      setBranches((prevBranches) =>
        prevBranches.map((branch) =>
          branch.branchId === branchId
            ? {
                ...branch,
                status: newStatus === "ENABLED" ? "Enabled" : "Disabled",
              }
            : branch,
        ),
      );
    } catch (error) {
      console.error("Error toggling branch status:", error);
    }
  };

  const getCountryCodeFromName = (countryName) => {
    if (!countryName) return "IN";
    if (countryName.length === 2) return countryName;
    const found = Country.getAllCountries().find(
      (c) => c.name.toLowerCase() === countryName.toLowerCase(),
    );
    return found ? found.isoCode : "IN";
  };

  const getStateCodeFromName = (countryCode, stateName) => {
    if (!stateName) return "";
    if (stateName.length <= 3) return stateName;
    const states = State.getStatesOfCountry(countryCode);
    const found = states.find(
      (s) => s.name.toLowerCase() === stateName.toLowerCase(),
    );
    return found ? found.isoCode : "";
  };

  const getCountryNameFromCode = (countryCode) => {
    if (!countryCode) return "India";
    if (countryCode.length > 2) return countryCode;
    const found = Country.getAllCountries().find(
      (c) => c.isoCode === countryCode,
    );
    return found ? found.name : "India";
  };

  const getStateNameFromCode = (countryCode, stateCode) => {
    if (!stateCode) return "";
    if (stateCode.length > 3) return stateCode;
    const states = State.getStatesOfCountry(countryCode);
    const found = states.find((s) => s.isoCode === stateCode);
    return found ? found.name : "";
  };

  const handleEdit = (branch) => {
    const countryCode = getCountryCodeFromName(branch.branchAddress?.country);
    const stateCode = getStateCodeFromName(
      countryCode,
      branch.branchAddress?.state,
    );
    const matchedDepartmentIds = departments
      .filter((dept) =>
        branch.departmentNames.some(
          (name) =>
            name.trim().toLowerCase() ===
            dept.departmentName.trim().toLowerCase(),
        ),
      )
      .map((dept) => dept.departmentId);

    setEditBranch({
      branchId: branch.branchId,
      branchName: branch.branchName,
      branchState: branch.branchState,
      companyId: branch.companyId,
      branchAddress: {
        addressId: branch.branchAddress?.addressId || 0,
        addressLine1: branch.branchAddress?.addressLine1 || "",
        addressLine2: branch.branchAddress?.addressLine2 || "",
        country: countryCode,
        state: stateCode,
        city: branch.branchAddress?.city || "",
        pincode: branch.branchAddress?.pincode || "",
      },
      departmentIds: matchedDepartmentIds,
      status: branch.status,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    setShowForm(true);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    const countryName = getCountryNameFromCode(values.branchAddress.country);
    const stateName = getStateNameFromCode(
      values.branchAddress.country,
      values.branchAddress.state,
    );
    const branchData = {
      branchId: editBranch ? editBranch.branchId : 0,
      branchName: values.branchName,
      status: "ENABLED",
      branchState: stateName,
      companyId: process.env.REACT_APP_COMPANY_ID,
      branchAddress: {
        addressId: values.branchAddress.addressId || 0,
        addressLine1: values.branchAddress.addressLine1,
        addressLine2: values.branchAddress.addressLine2,
        country: countryName,
        state: stateName,
        city: normalizeCityName(values.branchAddress.city),
        pincode: values.branchAddress.pincode,
      },
      departmentIds: Array.isArray(values.departmentIds)
        ? values.departmentIds.map((id) =>
            typeof id === "object" ? id.value : id,
          )
        : values.departmentIds,
    };

    try {
      if (editBranch) {
        await axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/branches`,
          branchData,
        );
        toast.success("Branch updated successfully!");
      } else {
        await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/branches`,
          branchData,
        );
        toast.success("Branch created successfully!");
      }
      await fetchBranches();
      setEditBranch(null);
      setShowForm(false);
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 400:
            setError("Invalid data. Please check the input fields.");
            break;
          case 401:
            setError("Unauthorized. Please log in.");
            break;
          case 403:
            setError(
              "Access denied. You don't have permission to perform this action.",
            );
            break;
          case 404:
            setError("Branch not found.");
            break;
          case 409:
            setError(
              "Branch already exists. Please use a different branch name.",
            );
            break;
          case 500:
            setError("Server error. Please try again later.");
            break;
          default:
            setError("An unexpected error occurred. Please try again.");
        }
      } else {
        setError(
          "Network error. Please check your internet connection and try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (branchId) => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/branches/${branchId}`,
      );
      setBranches((prevBranches) =>
        prevBranches.filter((branch) => branch.branchId !== branchId),
      );
      setEditBranch(null);
      fetchBranches();
      toast.success("Branch deleted successfully!");
    } catch (error) {
      console.error("Error deleting branch:", error);
    }
  };

  const handleStatusChange = async (branchId, newStatus) => {
    const action = newStatus === "ENABLED" ? "enable" : "disable";
    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/branches/${action}/${branchId}`,
      );
      await fetchBranches();
    } catch (error) {
      setError("Error updating branch status. Please try again.");
      throw error;
    }
  };

  const displayedBranches = branches;
  const isAllSelected =
    displayedBranches.length > 0 &&
    displayedBranches.every((branch) =>
      selectedBranches.includes(branch.branchId),
    );

  const getTableColumns = () => {
    return [
      {
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <ColoredCheckbox
              checked={isAllSelected}
              onChange={handleSelectAll}
              color="green"
              name="selectAll"
            />
            Branches
          </div>
        ),
        accessor: "branchName",
      },
      { header: "Address", accessor: "address" },
      { header: "State", accessor: "state" },
      { header: "City", accessor: "city" },
      { header: "Status", accessor: "status" },
      { header: "Actions", accessor: "actions" },
    ];
  };

  const getFormattedTableData = () => {
    const sortedBranches = [...displayedBranches].sort((a, b) => {
      if (a.status === "Enabled" && b.status !== "Enabled") return -1;
      if (a.status !== "Enabled" && b.status === "Enabled") return 1;
      return 0;
    });
    return sortedBranches.map((branch) => ({
      branchName: (
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <ColoredCheckbox
            checked={selectedBranches.includes(branch.branchId)}
            onChange={(event) => handleCheckboxChange(branch.branchId, event)}
            color="green"
            name={`branch-${branch.branchId}`}
          />
          <span>{branch.branchName || "N/A"}</span>
        </div>
      ),
      address: `${branch.branchAddress?.addressLine1 || "N/A"}${
        branch.branchAddress?.addressLine2
          ? `, ${branch.branchAddress.addressLine2}`
          : ""
      }`,
      state: branch.branchAddress?.state || "N/A",
      city: branch.branchAddress?.city || "N/A",
      status: (
        <ToggleButton
          isOn={branch.status === "Enabled"}
          onToggle={() => handleToggle(branch.branchId, branch.status)}
          status={branch.status === "Disabled"}
        />
      ),
      actions: (
        <div className="action-buttons">
          <IconButton
            icon="iconamoon:edit-light"
            className="branch-edit"
            onClick={() => handleEdit(branch)}
            variant="edit"
            title="Edit Branch"
          />
          <IconButton
            icon="fluent:delete-28-regular"
            className="branch-delete"
            onClick={() => handleDelete(branch.branchId)}
            variant="delete"
            title="Delete Branch"
          />
        </div>
      ),
    }));
  };

  return (
    <div className="branch-container">
      <h2 className="display6">Branch</h2>
      {error && <div className="error-message">{error}</div>}

      <div className="branch-header">
        <button
          className="create-role-button"
          onClick={() => {
            if (showForm) {
              setShowForm(false);
              setEditBranch(null);
            } else {
              setEditBranch(null);
              setShowForm(true);
            }
          }}
          style={showForm ? { backgroundColor: "#ff6692", color: "#fff" } : {}}
        >
          {!showForm && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
          )}
          {showForm ? "Close Branch Form" : "Create Branch"}
        </button>
        <div className="bulk-delete-wrapper">
          <ButtonWithIcon
            icon="fluent:delete-28-regular"
            text={`Bulk Delete (${selectedBranches.length})`}
            className="cancel-button"
            onClick={handleBulkDeleteClick}
            disabled={selectedBranches.length === 0}
          />
        </div>
      </div>

      {showForm && (
        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
          key={editBranch ? editBranch.branchId : "new"}
        >
          {({ setFieldValue, values }) => (
            <Form className="w-full p-4 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
              <h3 className="text-xl font-semibold text-gray-900 mb-1">
                {editBranch ? "Edit Branch" : "Create New Branch"}
              </h3>
              <div className="space-y-2 mt-3">
                <label className="block font-medium text-sm text-gray-700">
                  Branch Name
                </label>
                <Field
                  name="branchName"
                  placeholder="Eg: Bangalore Office"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-blue-500"
                />
                <ErrorMessage
                  name="branchName"
                  component="div"
                  className="text-red-500 text-sm"
                />
              </div>
              <div className="rounded-xl mt-3">
                <h4 className="font-semibold text-gray-700 mb-3">
                  📍 Address Details
                </h4>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Field
                      name="branchAddress.addressLine1"
                      placeholder="Address Line 1"
                      className="w-full border rounded-xl px-3 py-2.5"
                    />
                    <ErrorMessage
                      name="branchAddress.addressLine1"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div>
                    <Field
                      name="branchAddress.addressLine2"
                      placeholder="Address Line 2"
                      className="w-full border rounded-xl px-3 py-2.5"
                    />
                    <ErrorMessage
                      name="branchAddress.addressLine2"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div>
                    <Field
                      name="branchAddress.pincode"
                      placeholder="Pincode"
                      className="w-full border rounded-xl px-3 py-2.5"
                    />
                    <ErrorMessage
                      name="branchAddress.pincode"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div>
                    <Field
                      as="select"
                      name="branchAddress.country"
                      className="w-full border rounded-xl px-3 py-2.5"
                    >
                      <option value="">Select Country</option>
                      {Country.getAllCountries().map((country) => (
                        <option key={country.isoCode} value={country.isoCode}>
                          {country.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="branchAddress.country"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div>
                    <Field
                      as="select"
                      name="branchAddress.state"
                      className="w-full border rounded-xl px-3 py-2.5"
                    >
                      <option value="">Select State</option>
                      {State.getStatesOfCountry(
                        values.branchAddress.country,
                      ).map((state) => (
                        <option key={state.isoCode} value={state.isoCode}>
                          {state.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="branchAddress.state"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div>
                    <Field
                      as="select"
                      name="branchAddress.city"
                      className="w-full border rounded-xl px-3 py-2.5"
                      onChange={(e) => {
                        const selectedCity = e.target.value;
                        const normalizedCity = normalizeCityName(selectedCity);
                        setFieldValue("branchAddress.city", normalizedCity);
                      }}
                    >
                      <option value="">Select City</option>
                      {City.getCitiesOfState(
                        values.branchAddress.country,
                        values.branchAddress.state,
                      ).map((city) => (
                        <option key={city.name} value={city.name}>
                          {city.name === "Bangalore" ? "Bengaluru" : city.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="branchAddress.city"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <label className="block font-medium text-sm text-gray-700">
                  🏷️ Departments
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={`px-3 py-1 rounded-full border text-sm ${
                      departments.every((d) =>
                        values.departmentIds.includes(d.departmentId),
                      )
                        ? "border-transparent"
                        : "border-gray-300 hover:bg-gray-100"
                    }`}
                    style={{
                      backgroundColor: departments.every((d) =>
                        values.departmentIds.includes(d.departmentId),
                      )
                        ? "var(--active-color)"
                        : "transparent",
                      color: departments.every((d) =>
                        values.departmentIds.includes(d.departmentId),
                      )
                        ? "var(--text-inverse)"
                        : "var(--text-primary)",
                      fontWeight: "600",
                    }}
                    onClick={() => {
                      const allIds = departments.map((d) => d.departmentId);
                      const isAllSelected = allIds.every((id) =>
                        values.departmentIds.includes(id),
                      );
                      setFieldValue(
                        "departmentIds",
                        isAllSelected ? [] : allIds,
                      );
                    }}
                  >
                    {departments.every((d) =>
                      values.departmentIds.includes(d.departmentId),
                    )
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                  {departments.map((dept) => {
                    const isSelected = values.departmentIds.includes(
                      dept.departmentId,
                    );
                    return (
                      <button
                        key={dept.departmentId}
                        type="button"
                        onClick={() => {
                          const updated = isSelected
                            ? values.departmentIds.filter(
                                (id) => id !== dept.departmentId,
                              )
                            : [...values.departmentIds, dept.departmentId];
                          setFieldValue("departmentIds", updated);
                        }}
                        className={`px-3 py-1 rounded-full border text-sm ${
                          isSelected
                            ? "bg-blue-500 text-white border-blue-500"
                            : "bg-gray-100 text-gray-700 border-gray-300"
                        }`}
                      >
                        {dept.departmentName}
                      </button>
                    );
                  })}
                </div>
                <ErrorMessage
                  name="departmentIds"
                  component="div"
                  className="text-red-500 text-sm"
                />
              </div>
              <div className="flex justify-start">
                <ButtonWithIcon
                  type="submit"
                  icon="tdesign:send"
                  text={editBranch ? "Update Branch" : "Add Branch"}
                  className="submit-button"
                  disabled={loading}
                />
              </div>
            </Form>
          )}
        </Formik>
      )}

      <GeneralTable
        columns={getTableColumns()}
        data={getFormattedTableData()}
        title="Branches"
        selectedCount={selectedBranches.length}
        showPagination={true}
      />
      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onDecline={() => setShowWarningPopup(false)}
        onAccept={handleBulkDeleteSubmit}
        title="Confirm Bulk Delete"
        message={`Are you sure you want to delete ${selectedBranches.length} branch(es)? This action cannot be undone.`}
        acceptText="Yes, Delete"
        declineText="Cancel"
      />
    </div>
  );
};

export default Branch;
